<template>
    <div>
        <div class="card">
            <header class="card-head">
                <div>
                    <div class="card-title">{{ payload.title }}</div>
                    <div v-if="payload.subtitle" class="card-sub">{{ payload.subtitle }}</div>
                </div>
                <div class="card-side">
                    <div v-if="payload.rightLabel" class="card-sub">{{ payload.rightLabel }}</div>
                    <div v-if="hasDelta" class="legend">
                        <span class="legend-dot legend-dot--pos" />
                        <span>рост</span>
                        <span class="legend-dot legend-dot--neg" />
                        <span>снижение</span>
                    </div>
                </div>
            </header>

            <div class="rows">
                <div
                    v-for="(row, idx) in visibleRows"
                    :key="row.name + '-' + idx"
                    class="row"
                    :class="{ 'row--top': idx < 3, 'has-delta': hasDelta }"
                >
                    <div class="rank">{{ row.rank }}</div>
                    <div class="name" :title="row.name">{{ row.name }}</div>
                    <div class="bar">
                        <div
                            class="bar-fill"
                            :class="`bar-fill--rank${Math.min(idx + 1, 4)}`"
                            :style="{ width: row.barPct + '%' }"
                        />
                    </div>
                    <div class="val">{{ row.formattedValue }}</div>
                    <div
                        v-if="hasDelta"
                        class="delta"
                        :class="row.tone === 'neg' ? 'tone-neg' : (row.tone === 'pos' ? 'tone-pos' : 'tone-neutral')"
                    >
                        {{ row.delta || '—' }}
                    </div>
                </div>
            </div>

            <button
                v-if="rows.length > collapseLimit"
                type="button"
                class="toggle"
                @click="expanded = !expanded"
            >
                {{ expanded
                    ? 'Свернуть'
                    : `Показать ещё ${rows.length - collapseLimit} ${regionsWord(rows.length - collapseLimit)}` }}
            </button>

            <div v-if="payload.footer" class="footer">{{ payload.footer }}</div>
        </div>

        <!--
            Insights иногда приходят как строка (старая схема), иногда как объект
            { title, paragraphs[] }. Нормализуем через computed `insights` и рендерим
            только если действительно есть содержимое — иначе пустая красная полоска
            от `.strip::before` создаёт визуальный мусор.
        -->
        <div v-if="insights" class="insights">
            <div v-if="insights.title" class="strip">{{ insights.title }}</div>
            <p
                v-for="(p, idx) in insights.paragraphs"
                :key="idx"
                class="insight-text"
            >
                {{ p }}
            </p>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
    payload: { type: Object, required: true },
})

const expanded = ref(false)
const collapseLimit = 10

// Нормализуем строки: парсим числовое значение, считаем процент относительно максимума,
// форматируем число (тыс/млн), помечаем top-3. Это лечит ситуацию, когда LLM прислала
// одинаковые pct (или не прислала вовсе), из-за чего все полосы выглядели одинаково.
function parseNumeric(value) {
    if (typeof value === 'number') return value
    if (value == null) return null
    const cleaned = String(value).replace(/[^\d.,-]/g, '').replace(/\s+/g, '').replace(/,/g, '.')
    const n = Number(cleaned)
    return Number.isFinite(n) ? n : null
}

function formatNumber(n) {
    if (n == null || !Number.isFinite(n)) return '—'
    const abs = Math.abs(n)
    if (abs >= 1_000_000) return (n / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 2 }) + ' млн'
    if (abs >= 10_000) return (n / 1000).toLocaleString('ru-RU', { maximumFractionDigits: 1 }) + ' тыс'
    return n.toLocaleString('ru-RU')
}

const rows = computed(() => {
    const raw = Array.isArray(props.payload?.rows) ? props.payload.rows : []
    const parsed = raw.map(r => ({
        name: r?.name || '—',
        numeric: parseNumeric(r?.value),
        rawValue: r?.value,
        delta: r?.delta || '',
        tone: r?.tone || (r?.delta && /^-/.test(String(r.delta)) ? 'neg' : (r?.delta ? 'pos' : 'neutral')),
        explicitPct: typeof r?.pct === 'number' ? r.pct : null,
    }))

    // Безопасный максимум: либо реальный max(value), либо max(explicitPct) > 0.
    const maxNumeric = parsed.reduce((m, r) => (r.numeric != null && r.numeric > m ? r.numeric : m), 0)
    const maxPct = parsed.reduce((m, r) => (r.explicitPct != null && r.explicitPct > m ? r.explicitPct : m), 0)
    // Если LLM прислала pct, но они все одинаковые (например, все 100), относимся к ним
    // с подозрением — считаем сами по value.
    const llmGivesUsefulPct = maxPct > 0 && parsed.some(r => (r.explicitPct ?? 0) < maxPct)

    return parsed.map((r, idx) => {
        let pct
        if (maxNumeric > 0 && r.numeric != null) {
            pct = (r.numeric / maxNumeric) * 100
        } else if (llmGivesUsefulPct && r.explicitPct != null) {
            pct = (r.explicitPct / maxPct) * 100
        } else {
            pct = 0
        }
        // Минимум 3% — чтобы маленькие значения тоже были визуально различимы.
        const barPct = Math.max(3, Math.min(100, Math.round(pct)))
        return {
            ...r,
            rank: idx + 1,
            formattedValue: formatNumber(r.numeric ?? (typeof r.rawValue === 'string' ? null : Number(r.rawValue))),
            barPct,
        }
    })
})

const visibleRows = computed(() => (expanded.value ? rows.value : rows.value.slice(0, collapseLimit)))

const hasDelta = computed(() => rows.value.some(r => r.delta && r.delta !== '—'))

const insights = computed(() => {
    const raw = props.payload?.insights
    if (!raw) return null
    if (typeof raw === 'string') {
        const trimmed = raw.trim()
        if (!trimmed) return null
        return { title: '', paragraphs: [trimmed] }
    }
    if (typeof raw === 'object') {
        const title = (raw.title || '').trim()
        const paragraphs = Array.isArray(raw.paragraphs)
            ? raw.paragraphs.map(p => (typeof p === 'string' ? p.trim() : '')).filter(Boolean)
            : []
        if (!title && paragraphs.length === 0) return null
        return { title, paragraphs }
    }
    return null
})

function regionsWord(n) {
    const abs = Math.abs(n) % 100
    const last = abs % 10
    if (abs >= 11 && abs <= 14) return 'регионов'
    if (last === 1) return 'регион'
    if (last >= 2 && last <= 4) return 'региона'
    return 'регионов'
}
</script>

<style scoped>
.card {
    background: white;
    border: 1px solid #E8E6E0;
    border-radius: 12px;
    padding: 18px 20px;
    margin: 12px 0;
}

.card-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 14px;
    gap: 12px;
    flex-wrap: wrap;
}

.card-title {
    font-size: 14px;
    font-weight: 700;
    color: #23282C;
    line-height: 1.3;
}

.card-sub {
    font-size: 11px;
    color: #888780;
    margin-top: 4px;
}

.card-side {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
}

.legend {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 10.5px;
    color: #888780;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
}

.legend-dot--pos { background: #0F6E56; }
.legend-dot--neg { background: #E30613; }

.rows {
    display: flex;
    flex-direction: column;
}

.row {
    display: grid;
    grid-template-columns: 22px minmax(110px, 180px) 1fr 80px;
    align-items: center;
    column-gap: 12px;
    padding: 7px 0;
    font-size: 12.5px;
    border-bottom: 1px solid #F4F2EC;
}

.row.has-delta {
    grid-template-columns: 22px minmax(110px, 180px) 1fr 80px 60px;
}

.row:last-child {
    border-bottom: none;
}

.rank {
    color: #B7B5AF;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    text-align: right;
}

.row--top .rank {
    color: #E30613;
    font-weight: 700;
}

.name {
    color: #23282C;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.row--top .name {
    font-weight: 600;
}

.bar {
    height: 8px;
    background: #F4F2EC;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
}

.bar-fill {
    height: 100%;
    border-radius: 6px;
    transition: width 0.8s cubic-bezier(.2,.7,.2,1);
}

.bar-fill--rank1 { background: linear-gradient(90deg, #E30613, #FF6259); }
.bar-fill--rank2 { background: linear-gradient(90deg, #C0050F, #FF6259); }
.bar-fill--rank3 { background: linear-gradient(90deg, #4F5256, #888780); }
.bar-fill--rank4 { background: #888780; }
.bar-fill--rank5,
.bar-fill { background: #B7B5AF; }

.val {
    text-align: right;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
    color: #23282C;
}

.delta {
    text-align: right;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
}

.tone-pos { color: #0F6E56; }
.tone-neg { color: #E30613; }
.tone-neutral { color: #B7B5AF; }

.toggle {
    margin-top: 10px;
    background: transparent;
    border: 1px solid #E8E6E0;
    padding: 7px 14px;
    border-radius: 6px;
    font-family: inherit;
    font-size: 12px;
    color: #23282C;
    cursor: pointer;
    align-self: flex-start;
}

.toggle:hover { background: #F4F2EC; border-color: #C4C2BC; }

.footer {
    font-size: 10.5px;
    color: #888780;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px dashed #F1EFE8;
}

.insights { margin: 14px 0 4px; }

.strip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    margin: 6px 0 10px;
    color: #23282C;
    text-transform: uppercase;
}

.strip::before {
    content: '';
    width: 18px;
    height: 2px;
    background: #E30613;
    display: inline-block;
}

.insight-text {
    font-size: 13px;
    line-height: 1.65;
    color: #23282C;
    margin: 0 0 8px 0;
}

@media (max-width: 600px) {
    .row {
        grid-template-columns: 20px 1fr auto;
        column-gap: 8px;
    }
    .bar { display: none; }
    .val { font-size: 12px; }
}
</style>
